<script setup lang="ts">
import BmBtn from "@/components/controls/input/BmBtn.vue";
import errorLoggerService from "@/app/errors/errorLoggerService";
import { makeRef } from "@/components/composables/reactivityTypes";
import type { CameraDevice } from "html5-qrcode/camera/core";
import { Html5QrcodeScanType, Html5Qrcode } from "html5-qrcode";
import { throttle } from "lodash";
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import messages, { keys } from "@/plugins/i18n";
import type { BmUiState } from "@/components/controls/input/state/BmUIStateRepository";
import { BmUiStateRepository } from "@/components/controls/input/state/BmUIStateRepository";
import { screenSize } from "@/vue23switching/screenSize";
import type { QrDimensions } from "html5-qrcode/esm/core";

export interface Html5QrStreamEvents {
  (event: "decoded", value: string): void;
  (event: "not-supported"): void;
  (event: "loaded"): void;
}

interface Html5QrStreamState extends BmUiState {
  lastSelectedCameraDeviceId: string;
}

let qrboxFunction = (
  viewfinderWidth: number,
  viewfinderHeight: number
): QrDimensions => {
  if (!screenSize.smAndDown) {
    return {
      width: viewfinderWidth,
      height: viewfinderHeight,
    };
  }
  let minEdgePercentage = 0.8; // 80%
  let minEdgeSize = Math.min(viewfinderWidth, viewfinderHeight);
  let qrboxSize = Math.floor(minEdgeSize * minEdgePercentage);
  return {
    width: qrboxSize,
    height: qrboxSize,
  };
};

const TIME_BETWEEN_SCANS_MS = 1500;
const emit = defineEmits<Html5QrStreamEvents>();
const uiStateKey = "html5-qr-stream-key" as const;
const config = {
  fps: 10,
  supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
  showTorchButtonIfSupported: true,
  rememberLastUsedCamera: true,
  qrbox: qrboxFunction,
};

let html5QrCode: Html5Qrcode;
const cameraDevices = makeRef<readonly CameraDevice[]>([]);
const selectedCameraDeviceId = ref<string | null>(null);

const loadUiState = () => {
  const uiState = BmUiStateRepository.load<Html5QrStreamState>(uiStateKey);

  if (uiState.lastSelectedCameraDeviceId) {
    const matchedDevice = cameraDevices.value.find(
      (cameraDevice) => cameraDevice.id === uiState.lastSelectedCameraDeviceId
    );

    selectedCameraDeviceId.value = matchedDevice?.id ?? null;
  }
};

const onDecode = (decodedText: string) => {
  emit("decoded", decodedText);
};

const throttledOnDecode = throttle(onDecode, TIME_BETWEEN_SCANS_MS, {
  leading: true,
  trailing: false,
});

// Functions to start and stop the camera are async, and will continue to resolve
// outside the context of a Vue component. It is possible for the camera stop
// function to be called while the camera is still turning on, e.g., when this
// component is unmounted, so we need to track whether the camera "should" be on
// or off at any given time.
let cameraShouldBeOn = false;

const startCamera = async (cameraDeviceId: string) => {
  cameraShouldBeOn = true;

  try {
    await html5QrCode.start(
      cameraDeviceId,
      config,
      (decodedText, result) => throttledOnDecode(decodedText),
      undefined // Avoid error handling for now as it spams just when the camera is open
    );

    if (!cameraShouldBeOn) {
      await stopCamera();
    }
  } catch (error: unknown) {
    if (error instanceof Error || typeof error === "string") {
      errorLoggerService.logError("Could not start html 5 scanner", error);
    }
    emit("not-supported");
  }
};

const stopCamera = async () => {
  cameraShouldBeOn = false;

  try {
    // @ts-expect-error (stateManagerProxy is private)
    if (html5QrCode.stateManagerProxy.isScanning()) {
      await html5QrCode.stop();
    }
  } catch (error: unknown) {
    if (error instanceof Error || typeof error === "string") {
      errorLoggerService.logError("Could not stop html5 scanner", error);
    }
  }
};

const openSettings = () => {
  selectedCameraDeviceId.value = null;
};

onMounted(async () => {
  try {
    html5QrCode = new Html5Qrcode("scanner");
    cameraDevices.value = await Html5Qrcode.getCameras();
    loadUiState();

    // Default to only camera, if we only have one and our uiState hasn't selected
    if (cameraDevices.value.length === 1 && !selectedCameraDeviceId.value) {
      selectedCameraDeviceId.value = cameraDevices.value[0].id;
    }
  } catch (error: unknown) {
    if (error instanceof Error || typeof error === "string") {
      errorLoggerService.logError("Could not initialise html 5 scanner", error);
    }
    emit("not-supported");
  }
});

watch(selectedCameraDeviceId, async (newCameraDeviceId, oldCameraDeviceId) => {
  if (newCameraDeviceId) {
    await startCamera(newCameraDeviceId);

    BmUiStateRepository.save<Html5QrStreamState>(uiStateKey, {
      lastSelectedCameraDeviceId: newCameraDeviceId,
    });
  } else {
    await stopCamera();

    BmUiStateRepository.save<Html5QrStreamState>(uiStateKey, {
      lastSelectedCameraDeviceId: "",
    });
  }
});

onBeforeUnmount(async () => {
  await stopCamera();
});
</script>

<template>
  <v-container fluid pa-0>
    <v-row dense align="center" justify="center">
      <v-col cols="12">
        <div id="scanner" data-qa="camera-scanner">
          <v-container v-if="!cameraDevices.length" fluid>
            <v-row justify="center">
              <v-col cols="auto">
                {{ messages.scanner_loading }}
                <v-progress-circular class="ml-2" indeterminate />
              </v-col>
            </v-row>
          </v-container>
        </div>
      </v-col>
      <v-col v-if="selectedCameraDeviceId" class="settings-button" cols="auto">
        <bm-btn icon button-type="secondary" @click="openSettings">
          <bm-icon icon="Settings" />
        </bm-btn>
      </v-col>
      <v-col v-if="!selectedCameraDeviceId && cameraDevices.length" cols="8">
        <bm-dropdown
          :value.sync="selectedCameraDeviceId"
          :t="keys.scanner_select_your_camera"
          :items="cameraDevices"
          :error="!selectedCameraDeviceId"
          item-text="label"
          item-value="id"
          hide-details
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="sass">
.settings-button
  position: absolute
  top: 0
  right: 0
  transform: translate(-25%, 25%)
</style>
